import { Alert, Badge, Button, Card, Dropdown, LearnMoreLink, Text } from '@ui/themed';
import { t } from 'i18next';
import type { DropdownOption } from '../../../../components/ui/dropdown/types';
import { PRICE_LIST_SOURCES, type PriceList, useAllPriceLists } from '../../../price-lists';
import { SETTING_KEY_TO_LABEL } from '../../constants';
import { useCustomerGroupBuilderForm } from '../../hooks/use-customer-group-builder-form';
import type { SettingPriceListState } from '../../types/customer-group.schema';
import { ResetSettingsButton } from './reset-settings-button';

function transformPriceList(priceList: PriceList[], state: SettingPriceListState) {
  // if multiple price lists (more than 1) selected, restrict user to only choose price lists based on currency code.
  const selectedCurrencyCode =
    state.length > 1
      ? priceList.find((list) => list.slug === state[0]?.price_list_slug)?.currency_code
      : undefined;

  return priceList
    .filter(
      (priceList: PriceList) =>
        priceList.source !== PRICE_LIST_SOURCES.PLATFORM &&
        (!selectedCurrencyCode ||
          (!!selectedCurrencyCode && priceList.currency_code === selectedCurrencyCode)),
    )
    .map(
      (priceList: PriceList): DropdownOption => ({
        value: priceList.slug,
        label: `${priceList.currency_code.toUpperCase()} - ${priceList.name}`,
      }),
    );
}

export function PriceListsCard() {
  const form = useCustomerGroupBuilderForm();
  const { data: priceLists } = useAllPriceLists();

  return (
    <form.Field name="settings.priceListSelection">
      {(field) => (
        <Card showBorder>
          <Card.Header collapsible headingLevel="h2" icon="priceList">
            {SETTING_KEY_TO_LABEL.priceListSelection}
          </Card.Header>
          <Card.Body>
            <Text variant="secondary">
              {t('features:customer_group.components.price_lists_card.description')}{' '}
              <LearnMoreLink link="https://docs.sparklayer.io/customers#x50eg" />
            </Text>
            {field.state.value?.map((priceList, index: number) => (
              <fieldset
                // biome-ignore lint/suspicious/noArrayIndexKey: slug can be empty or duplicated before selection
                key={`${priceList.price_list_slug}-${index}`}
                className="grid grid-cols-[auto_1fr_auto] items-center gap-2"
              >
                <Badge variant="default">{String(index + 1)}</Badge>
                <form.Field name={`settings.priceListSelection[${index}]`}>
                  {(subField) => (
                    <Dropdown
                      options={transformPriceList(priceLists, field.state.value ?? [])}
                      onChange={(selected) => subField.handleChange({ price_list_slug: selected })}
                      placeholder={t(
                        'features:customer_group.components.price_lists_card.select.placeholder',
                      )}
                      value={priceList.price_list_slug}
                    />
                  )}
                </form.Field>
                <Button
                  icon="delete"
                  variant="dangerOutline"
                  onClick={() => field.removeValue(index)}
                />
              </fieldset>
            ))}
            {!field.state.meta.isValid && (
              <Alert
                variant="warning"
                title={t('features:customer_group.components.price_lists_card.alert.title')}
                headingLevel="h3"
              >
                {field.state.meta.errors[0]?.message.match('\n')
                  ? field.state.meta.errors[0]?.message.split('\n').map((error: string) => (
                      <>
                        {error}
                        <br />
                      </>
                    ))
                  : field.state.meta.errors[0]?.message}
              </Alert>
            )}
            <div className="flex justify-start gap-3">
              <Button
                onClick={() => {
                  const addPriceList = field.state.value?.concat({ price_list_slug: '' });
                  field.handleChange(addPriceList);
                }}
                variant="secondary"
              >
                {t('features:customer_group.components.price_lists_card.add')}
              </Button>
              <ResetSettingsButton field={field} />
            </div>
            <Text variant="secondary">
              {t('features:customer_group.components.price_lists_card.priority_info')}
            </Text>
          </Card.Body>
        </Card>
      )}
    </form.Field>
  );
}
